/**
 * 2026-05-25 — local-only experimental action rail for the
 * CryptoPriceStyleMarketView. Modern fintech treatment: round
 * icon-only chips for bookmark / share / embed alongside a primary
 * pill for "Post to feed". Borderless soft-gray surface, hover
 * darkens. Replace the production <MarketShareActions/> with this
 * once the visual treatment is locked.
 */
"use client";

import { useEffect, useState } from "react";
import { Share2, Link2, PenSquare, Check } from "lucide-react";
import { ComposerButton } from "@/components/ComposerButton";
import { loadTokens } from "@/lib/api";

interface Props {
  marketId: string;
  marketSlug: string;
}

const SURFACE = "#F2F3F5";
const SURFACE_HOVER = "#E7E9EC";
const TEXT = "#0A0B0D";
const ACCENT = "#1652F0";

export function ExperimentalMarketActions({ marketId, marketSlug }: Props) {
  const [copied, setCopied] = useState<"share" | "embed" | null>(null);
  // Post-to-feed is only meaningful for authed users — they need an
  // account to publish to. Null while hydrating to avoid SSR/CSR
  // mismatch flash. Anon users see only share + embed.
  const [authed, setAuthed] = useState<boolean | null>(null);
  useEffect(() => {
    setAuthed(loadTokens() != null);
  }, []);

  function copy(text: string, kind: "share" | "embed") {
    if (typeof navigator === "undefined" || !navigator.clipboard) return;
    void navigator.clipboard.writeText(text).then(() => {
      setCopied(kind);
      window.setTimeout(() => setCopied(null), 1500);
    });
  }

  const url =
    typeof window !== "undefined"
      ? `${window.location.origin}/markets/${marketSlug}`
      : `/markets/${marketSlug}`;
  const embed = `<iframe src="${url}/embed" width="640" height="420" frameborder="0"></iframe>`;

  return (
    <div className="flex flex-wrap items-center gap-1.5">
      <IconChip
        ariaLabel="Copy share link"
        onClick={() => copy(url, "share")}
        active={copied === "share"}
        title={copied === "share" ? "Link copied" : "Share"}
      >
        {copied === "share" ? (
          <Check size={15} strokeWidth={2} />
        ) : (
          <Share2 size={14} strokeWidth={1.75} />
        )}
      </IconChip>

      <IconChip
        ariaLabel="Copy embed code"
        onClick={() => copy(embed, "embed")}
        active={copied === "embed"}
        title={copied === "embed" ? "Embed copied" : "Embed"}
      >
        {copied === "embed" ? (
          <Check size={15} strokeWidth={2} />
        ) : (
          <Link2 size={14} strokeWidth={1.75} />
        )}
      </IconChip>

      <span
        className="mx-1 hidden h-5 w-px sm:inline-block"
        style={{ background: "#E1E4E8" }}
        aria-hidden="true"
      />

      {authed === true && (
        <ComposerButton
          ariaLabel="Post about this market"
          initialAttachedMarketId={marketId}
          className="inline-flex items-center gap-1.5 rounded-full bg-[#F2F3F5] px-3 py-1.5 text-[12px] font-semibold text-[#0A0B0D] transition-colors hover:bg-[#E7E9EC]"
        >
          <PenSquare size={12} strokeWidth={2} aria-hidden="true" />
          Post to feed
        </ComposerButton>
      )}
    </div>
  );
}

function IconChip({
  children,
  onClick,
  ariaLabel,
  title,
  active,
}: {
  children: React.ReactNode;
  onClick: () => void;
  ariaLabel: string;
  title: string;
  active?: boolean;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={ariaLabel}
      title={title}
      className="group relative inline-flex h-8 w-8 items-center justify-center rounded-full transition-all"
      style={{
        background: active ? "rgba(22, 82, 240, 0.10)" : SURFACE,
        color: active ? ACCENT : TEXT,
      }}
      onMouseEnter={(e) => {
        if (!active) e.currentTarget.style.background = SURFACE_HOVER;
      }}
      onMouseLeave={(e) => {
        if (!active) e.currentTarget.style.background = SURFACE;
      }}
    >
      {children}
    </button>
  );
}
